import { useCallback, useEffect, useRef, useState } from 'react';

import {
  deviceOrientationPermissionRequired,
  isDeviceOrientationSupported,
  shouldPromptForDeviceOrientation,
} from '../utils/device-orientation-platform';
import {
  getDeviceOrientationErrorMessage,
  requestDeviceOrientationPermission,
  watchDeviceHeading,
} from '../utils/get-device-heading';

type DeviceHeadingStatus =
  | 'idle'
  | 'unsupported'
  | 'requesting'
  | 'active'
  | 'error';

export function useDeviceHeading(enabled: boolean) {
  const [heading, setHeading] = useState<number | null>(null);
  const [orientationError, setOrientationError] = useState<string | null>(null);
  const [status, setStatus] = useState<DeviceHeadingStatus>('idle');
  const stopWatchRef = useRef<(() => void) | null>(null);
  const headingRef = useRef<number | null>(null);
  const isRequestingRef = useRef(false);

  const stopWatch = useCallback(() => {
    stopWatchRef.current?.();
    stopWatchRef.current = null;
  }, []);

  const startTracking = useCallback(async () => {
    if (!enabled || !isDeviceOrientationSupported()) {
      if (!isDeviceOrientationSupported()) {
        setStatus('unsupported');
      }
      return;
    }

    if (isRequestingRef.current) {
      return;
    }

    isRequestingRef.current = true;
    setOrientationError(null);
    setStatus('requesting');
    stopWatch();

    const granted = await requestDeviceOrientationPermission();
    if (!granted) {
      isRequestingRef.current = false;
      setStatus('error');
      setOrientationError(getDeviceOrientationErrorMessage(true));
      return;
    }

    stopWatchRef.current = watchDeviceHeading((value) => {
      headingRef.current = value;
      setHeading(value);
      setOrientationError(null);
      setStatus('active');
    });

    isRequestingRef.current = false;
    setStatus('active');
  }, [enabled, stopWatch]);

  useEffect(() => {
    if (!enabled) {
      stopWatch();
      setHeading(null);
      headingRef.current = null;
      setOrientationError(null);
      setStatus('idle');
      return;
    }

    if (!isDeviceOrientationSupported()) {
      setStatus('unsupported');
      return;
    }

    if (!deviceOrientationPermissionRequired()) {
      void startTracking();
    }

    return () => stopWatch();
  }, [enabled, startTracking, stopWatch]);

  const needsPermission =
    enabled &&
    shouldPromptForDeviceOrientation() &&
    (status === 'idle' || (status === 'error' && heading == null));

  return {
    heading,
    headingRef,
    orientationError,
    startTracking,
    needsPermission,
    isRequesting: status === 'requesting',
  };
}
